import { useTranslation } from "react-i18next";

import { SqlRbacAction } from "services/rbac/action";
import { RBAC_NAME_TYPE_MAP, SystemRole } from "services/rbac/constants";
import { PrivilegeObjectType, Role } from "services/rbac/types";

import { useEditActions } from "pages/govern/Roles/useEditActions";
import { useViewActions } from "pages/govern/Roles/useViewActions";

import { useMenu } from "components/ActionMenu/useMenu";
import { useAccessManager } from "components/App/accessManager";

import { RecordLabel } from "../RecordLabel";
import { Menu } from "../RecordMenu/RecordMenu";
import { RecordNode } from "../RecordNode";
import { useDeleteRole } from "./useDeleteRole";

import styles from "./styles.module.scss";

type Props = {
  role: Role;
};

export const useRoleMenuItems = ({
  menu,
  role,
}: {
  role: Role;
  menu: ReturnType<typeof useMenu>;
}) => {
  const { roleName } = role;
  const { t } = useTranslation();
  const { isAllowedTo } = useAccessManager();
  const { viewRoleMarkup, handleView } = useViewActions();

  const { deleteRoleMarkup, deleteRole } = useDeleteRole();

  const { editRoleMarkup, handleEdit } = useEditActions();

  const canDeleteRole = isAllowedTo(
    PrivilegeObjectType.role,
    role.roleName,
    SqlRbacAction.MODIFY,
    {
      owner: role.roleOwner,
    }
  );

  const systemDefined = RBAC_NAME_TYPE_MAP[roleName as SystemRole];

  const deleteItems =
    canDeleteRole && !systemDefined
      ? [
          [
            {
              key: "delete",
              text: t("workspace.roles_menu.delete"),
              action: () => {
                deleteRole(role.roleName);
                menu.closeMenu();
              },
              testId: "delete-role-item",
            },
          ],
        ]
      : [];

  const items = [
    [
      ...(systemDefined
        ? [
            {
              key: "view",
              text: t("workspace.roles_menu.view_grants"),
              action: () => {
                handleView(role);
                menu.closeMenu();
              },
              testId: "view-grants-item",
            },
          ]
        : [
            {
              key: "edit",
              text: t("workspace.roles_menu.edit"),
              action: () => {
                handleEdit(role);
                menu.closeMenu();
              },
              testId: "edit-role-item",
            },
          ]),
    ],
    ...deleteItems,
  ];

  return {
    items,
    modalsMarkup: (
      <>
        {viewRoleMarkup}
        {editRoleMarkup}
        {deleteRoleMarkup}
      </>
    ),
  };
};

export const RoleRecord = (props: Props) => {
  const { role } = props;
  const { roleName } = role;
  const menu = useMenu();

  const { items, modalsMarkup } = useRoleMenuItems({
    role,
    menu,
  });

  return (
    <>
      <RecordNode className={styles.node}>
        <div className={styles.node__inner}>
          <RecordLabel
            className={styles.label}
            testId={`role-label-${roleName}`}
          >
            {roleName}
          </RecordLabel>
          <Menu
            testId="role-more-icon"
            items={items}
            menu={menu}
          />
        </div>
      </RecordNode>
      {modalsMarkup}
    </>
  );
};
